/**
 * CDP SDK Context Building
 * Page, browser, OS, screen, and device context
 */

import type {
  PageContext,
  ScreenContext,
  BrowserInfo,
  OSInfo,
  DeviceInfo,
  CampaignContext,
  EventContext,
  SessionData,
  DeviceFingerprint,
  AutomationSignals,
} from './types';
import { SDK_NAME, SDK_VERSION } from './types';
import { buildSessionContext, buildCampaignContext } from './session';

// =============================================================================
// PAGE CONTEXT
// =============================================================================

/**
 * Get page context from current location
 */
export function getPageContext(): PageContext {
  return {
    path: window.location.pathname,
    url: window.location.href,
    title: document.title,
    referrer: document.referrer,
    search: window.location.search || undefined,
  };
}

// =============================================================================
// SCREEN CONTEXT
// =============================================================================

/**
 * Get screen context
 */
export function getScreenContext(): ScreenContext {
  return {
    width: window.screen.width,
    height: window.screen.height,
    density: window.devicePixelRatio || 1,
    innerWidth: window.innerWidth,
    innerHeight: window.innerHeight,
  };
}

// =============================================================================
// BROWSER DETECTION
// =============================================================================

/**
 * Detect browser name and version from user agent
 */
export function getBrowserInfo(): BrowserInfo {
  const ua = navigator.userAgent;

  // Firefox
  if (ua.includes('Firefox/')) {
    const version = ua.split('Firefox/')[1]?.split(' ')[0] || '';
    return { name: 'Firefox', version };
  }

  // Edge (Chromium-based)
  if (ua.includes('Edg/')) {
    const version = ua.split('Edg/')[1]?.split(' ')[0] || '';
    return { name: 'Edge', version };
  }

  // Chrome
  if (ua.includes('Chrome/')) {
    const version = ua.split('Chrome/')[1]?.split(' ')[0] || '';
    return { name: 'Chrome', version };
  }

  // Safari
  if (ua.includes('Safari/')) {
    const version = ua.split('Version/')[1]?.split(' ')[0] || '';
    return { name: 'Safari', version };
  }

  // Opera
  if (ua.includes('OPR/') || ua.includes('Opera/')) {
    const version = ua.split('OPR/')[1]?.split(' ')[0] ||
                    ua.split('Opera/')[1]?.split(' ')[0] || '';
    return { name: 'Opera', version };
  }

  return { name: 'Unknown', version: '' };
}

// =============================================================================
// OS DETECTION
// =============================================================================

/**
 * Detect OS name and version from user agent
 */
export function getOSInfo(): OSInfo {
  const ua = navigator.userAgent;

  // Windows
  if (ua.includes('Windows')) {
    let version = '';
    if (ua.includes('Windows NT 10.0')) version = '10';
    else if (ua.includes('Windows NT 6.3')) version = '8.1';
    else if (ua.includes('Windows NT 6.2')) version = '8';
    else if (ua.includes('Windows NT 6.1')) version = '7';
    return { name: 'Windows', version };
  }

  // macOS
  if (ua.includes('Mac OS X') || ua.includes('Macintosh')) {
    const match = ua.match(/Mac OS X (\d+[._]\d+)/);
    const version = match ? match[1].replace('_', '.') : '';
    return { name: 'macOS', version };
  }

  // iOS
  if (ua.includes('iPhone') || ua.includes('iPad') || ua.includes('iPod')) {
    const match = ua.match(/OS (\d+[._]\d+)/);
    const version = match ? match[1].replace('_', '.') : '';
    return { name: 'iOS', version };
  }

  // Android
  if (ua.includes('Android')) {
    const match = ua.match(/Android (\d+\.?\d*)/);
    const version = match ? match[1] : '';
    return { name: 'Android', version };
  }

  // Linux
  if (ua.includes('Linux')) {
    return { name: 'Linux', version: '' };
  }

  // Chrome OS
  if (ua.includes('CrOS')) {
    return { name: 'Chrome OS', version: '' };
  }

  return { name: 'Unknown', version: '' };
}

// =============================================================================
// DEVICE TYPE DETECTION
// =============================================================================

/**
 * Detect device type from user agent
 */
export function getDeviceType(): 'desktop' | 'mobile' | 'tablet' {
  const ua = navigator.userAgent.toLowerCase();

  // Tablet detection (before mobile, as some tablets include "mobile")
  if (
    ua.includes('tablet') ||
    ua.includes('ipad') ||
    ua.includes('playbook') ||
    ua.includes('silk') ||
    (ua.includes('android') && !ua.includes('mobile'))
  ) {
    return 'tablet';
  }

  // Mobile detection
  if (
    ua.includes('mobile') ||
    ua.includes('iphone') ||
    ua.includes('ipod') ||
    ua.includes('android') ||
    ua.includes('blackberry') ||
    ua.includes('opera mini') ||
    ua.includes('iemobile') ||
    ua.includes('wpdesktop')
  ) {
    return 'mobile';
  }

  return 'desktop';
}

// =============================================================================
// DEVICE CONTEXT
// =============================================================================

/**
 * Build device context (without fingerprint - that's added separately)
 */
export function getDeviceContext(
  fingerprintId?: string,
  fingerprint?: DeviceFingerprint
): DeviceInfo | undefined {
  if (!fingerprintId) {
    return undefined;
  }

  return {
    id: fingerprintId,
    type: deviceTypeCached(),
    fingerprint: fingerprint?.components,
    confidence: fingerprint?.confidence,
  };
}

// =============================================================================
// LOCALE & TIMEZONE
// =============================================================================

/**
 * Get user's locale
 */
export function getLocale(): string {
  return navigator.language || 'en-US';
}

/**
 * Get user's timezone.
 *
 * Cached for the life of the page: `Intl.DateTimeFormat()` builds a fresh
 * formatter with a fresh options object every call, and this ran on EVERY
 * event. The server-side twin of this mistake cost 550 us of a 655 us event
 * fold (cdp 0d336d3, 12x) — here the cost lands on the reader's phone, five
 * times per article. The zone cannot change while the page is open.
 */
let _timezone: string | null = null;
export function getTimezone(): string {
  if (_timezone !== null) return _timezone;
  try {
    _timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    _timezone = 'UTC';
  }
  return _timezone;
}

// =============================================================================
// AUTOMATION / HEADLESS TELLS
// =============================================================================

/**
 * Collect lightweight headless / automation tells for server-side bot detection.
 * Every probe is wrapped defensively — a locked-down/hostile environment must
 * never throw from context building. Sent on every event; the server flags
 * webdriver===true (strong) and the headless-Chrome combo.
 */
export function getAutomationSignals(): AutomationSignals {
  let webdriver = false;
  let noChrome = false;
  let plugins = -1;
  let languages = -1;
  try {
    webdriver = (navigator as Navigator & { webdriver?: boolean }).webdriver === true;
  } catch { /* ignore */ }
  try {
    const ua = navigator.userAgent || '';
    // window.chrome exists in real Chrome/Chromium; headless Chrome often lacks
    // it. Only meaningful on a Chrome (non-Edge/Opera) UA.
    if (/chrome\//i.test(ua) && !/edg\/|opr\//i.test(ua)) {
      noChrome = !(window as unknown as { chrome?: unknown }).chrome;
    }
  } catch { /* ignore */ }
  try { plugins = navigator.plugins ? navigator.plugins.length : -1; } catch { /* ignore */ }
  try { languages = navigator.languages ? navigator.languages.length : -1; } catch { /* ignore */ }
  return { webdriver, noChrome, plugins, languages };
}

// =============================================================================
// FULL CONTEXT BUILDER
// =============================================================================

// The three UA parses below are constant for the page — and the server
// re-derives all three from the User-Agent header regardless (utils/device.ts
// parseBrowser/parseOS/detectDeviceType), so they are also the part of the
// context nothing reads. Memoised until they can be dropped outright.
let _browser: BrowserInfo | null = null;
let _os: OSInfo | null = null;
let _deviceType: 'desktop' | 'mobile' | 'tablet' | null = null;

function browserCached(): BrowserInfo {
  if (!_browser) _browser = getBrowserInfo();
  return _browser;
}
function osCached(): OSInfo {
  if (!_os) _os = getOSInfo();
  return _os;
}
function deviceTypeCached(): 'desktop' | 'mobile' | 'tablet' {
  if (!_deviceType) _deviceType = getDeviceType();
  return _deviceType;
}

/**
 * The reduced context for every event after the first of a page view
 * (SiteProfile.events.leanContext). It carries EXACTLY what the server reads
 * per row — nothing here may be dropped without checking the consumer:
 *
 *   page.url       -> ClickHouse page_url, and the `domain` key of FOUR
 *                     materialized views (news_metrics_daily_v2,
 *                     news_category_daily, article_readthrough,
 *                     reader_article_daily). Dropping it empties them.
 *   page.path      -> ClickHouse page_path
 *   page.referrer  -> ClickHouse referrer_domain
 *   session.id     -> session stats + the profile-write coalescing decision
 *   device.id      -> identity resolution (the client fingerprint)
 *   screen         -> detectClientAnomaly (zero-size screen = headless)
 *   automation     -> detectClientAnomaly (webdriver, missing window.chrome)
 *   campaign       -> ClickHouse utm_* (added 2026-09-21)
 *   emailClickId   -> ClickHouse email_click_id
 *
 * Everything else in the full context is either overwritten server-side,
 * re-derived from the User-Agent header, or read by nobody.
 */
export function buildLeanEventContext(
  session: SessionData,
  fingerprintId?: string
): EventContext {
  const page = getPageContext();
  return {
    page: { path: page.path, url: page.url, referrer: page.referrer },
    screen: getScreenContext(),
    device: fingerprintId ? { id: fingerprintId, type: deviceTypeCached() } : undefined,
    campaign: buildCampaignContext(session),
    emailClickId: session.emailClickId || null,
    automation: getAutomationSignals(),
    session: { id: buildSessionContext(session).id },
  } as unknown as EventContext;
}

// Whether a FULL context has already gone out for the current page view.
// Reset on every (soft-)navigation by resetContextEpoch(), so each page view
// carries one complete context and the rest of its events carry the lean one.
let _fullContextSent = false;

/** Start a new page view: the next event carries the full context again. */
export function resetContextEpoch(): void {
  _fullContextSent = false;
}

/**
 * The context for the next outgoing event.
 *
 * Full for the first event of a page view (and always, when leanContext is
 * off or the event is an identify — identity resolution and trait merging
 * read fields the lean shape does not carry). Lean for the rest.
 *
 * Note this is decided at BUILD time, not send time: an event that fails and
 * lands in the durable retry queue keeps whatever context it was built with.
 * That is safe because the lean shape already carries every field that reaches
 * a ClickHouse column — a retried lean event loses nothing a full one would
 * have stored.
 */
export function nextEventContext(
  session: SessionData,
  fingerprintId?: string,
  fingerprint?: DeviceFingerprint,
  opts?: { lean?: boolean; forceFull?: boolean }
): EventContext {
  if (!opts?.lean || opts.forceFull || !_fullContextSent) {
    return buildEventContext(session, fingerprintId, fingerprint);
  }
  return buildLeanEventContext(session, fingerprintId);
}

/**
 * Mark the page view's full context as DELIVERED.
 *
 * Deliberately called from processAndSendEvent (after consent and beforeSend),
 * not from nextEventContext. Building a payload is not sending it: on a cold
 * start the news plugin defers its `Article Viewed` until the remote profile
 * lands, so the core `page` event is built FIRST — and then cancelled by the
 * plugin's own beforeSend (suppressPageOnArticle). Marking at build time gave
 * that cancelled event the full context and left the real first event of the
 * page with the lean one.
 *
 * The cost of marking late is that two events built before either is accepted
 * both carry the full context. That is the right way round to be wrong.
 */
export function markFullContextSent(): void {
  _fullContextSent = true;
}

/**
 * Build complete event context
 */
export function buildEventContext(
  session: SessionData,
  fingerprintId?: string,
  fingerprint?: DeviceFingerprint
): EventContext {
  const campaignContext = buildCampaignContext(session);

  return {
    page: getPageContext(),
    screen: getScreenContext(),
    browser: browserCached(),
    os: osCached(),
    device: getDeviceContext(fingerprintId, fingerprint),
    locale: getLocale(),
    timezone: getTimezone(),
    userAgent: navigator.userAgent,
    campaign: campaignContext,
    session: buildSessionContext(session),
    library: {
      name: SDK_NAME,
      version: SDK_VERSION,
    },
    // Email-marketing attribution: surface session.emailClickId at the
    // top level of context so server-side joins (push email_events.event_id
    // ↔ cdp events.context.email_click_id ↔ ecommerce_orders) don't have
    // to dig into properties. Set on every event for the lifetime of the
    // sticky storage key, not just the immediate landing page.
    emailClickId: session.emailClickId || null,
    automation: getAutomationSignals(),
  } as EventContext;
}

// =============================================================================
// REFERRER UTILITIES
// =============================================================================

/**
 * Get the referring domain
 */
export function getReferringDomain(): string | null {
  if (!document.referrer) {
    return null;
  }
  try {
    const url = new URL(document.referrer);
    return url.hostname;
  } catch {
    return null;
  }
}

/**
 * Check if referrer is from a search engine
 */
export function isSearchReferrer(): boolean {
  const domain = getReferringDomain();
  if (!domain) {
    return false;
  }

  const searchEngines = [
    'google.',
    'bing.',
    'yahoo.',
    'duckduckgo.',
    'baidu.',
    'yandex.',
    'ecosia.',
  ];

  return searchEngines.some((engine) => domain.includes(engine));
}

/**
 * Check if referrer is from a social network
 */
export function isSocialReferrer(): boolean {
  const domain = getReferringDomain();
  if (!domain) {
    return false;
  }

  const socialNetworks = [
    'facebook.',
    'fb.',
    'twitter.',
    't.co',
    'instagram.',
    'linkedin.',
    'pinterest.',
    'reddit.',
    'tiktok.',
    'youtube.',
  ];

  return socialNetworks.some((network) => domain.includes(network));
}
